// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { env } from '@/lib/env';

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

const ORG_ID_STORAGE_KEY = 'bb_org_id';

export const getCachedOrgId = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ORG_ID_STORAGE_KEY);
};

export const setCachedOrgId = (orgId: string | null) => {
  if (typeof window === 'undefined') return;
  if (orgId) {
    localStorage.setItem(ORG_ID_STORAGE_KEY, orgId);
  } else {
    localStorage.removeItem(ORG_ID_STORAGE_KEY);
  }
};

const createSafeFetch = (supabaseKey: string) => {
  const key = (supabaseKey ?? '').trim();
  const shouldStripKeyAuthorization = key.startsWith('sb_');

  if (!shouldStripKeyAuthorization) return fetch;

  // supabase-js sets `Authorization: Bearer <supabaseKey>` by default.
  // That breaks with new `sb_*` keys because they're not JWTs, so upstream returns "Invalid JWT".
  // We strip that specific header value while preserving `apikey` and real user sessions.
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!init?.headers) return fetch(input, init);

    const headers = new Headers(init.headers);
    const auth = headers.get('Authorization');
    if (auth && auth.toLowerCase() === `bearer ${key}`.toLowerCase()) {
      headers.delete('Authorization');
      return fetch(input, { ...init, headers });
    }

    return fetch(input, init);
  };
};

const supabaseClient = createClient<Database>(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
  global: {
    fetch: createSafeFetch(env.SUPABASE_ANON_KEY),
  },
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});

const withOrgId = (value: any, orgId: string) => {
  if (Array.isArray(value)) {
    return value.map((item) => ({ ...item, org_id: item?.org_id ?? orgId }));
  }
  if (value && typeof value === 'object') {
    return { ...value, org_id: (value as { org_id?: string }).org_id ?? orgId };
  }
  return value;
};

// Error returned when org_id is not available
const createOrgIdError = () => ({
  error: {
    message: 'Organization context is required but not available. Please ensure you are logged in and have access to an organization.',
    code: 'ORG_ID_REQUIRED',
    details: null,
    hint: null,
  },
  data: null,
  count: null,
  status: 400,
  statusText: 'Bad Request',
});

export const fromSecure = <T extends keyof Database['public']['Tables']>(table: T) => {
  const orgId = getCachedOrgId();
  if (!orgId) {
    // Return a Supabase-like response object with error instead of throwing
    const errorResponse = createOrgIdError();
    const rejectedPromise = Promise.resolve(errorResponse);
    
    return {
      select: () => rejectedPromise,
      insert: () => rejectedPromise,
      upsert: () => rejectedPromise,
      update: () => rejectedPromise,
      delete: () => rejectedPromise,
    } as any;
  }
  const base = supabaseClient.from(table);
  return {
    ...base,
    select: (columns?: string, options?: { count?: 'exact' | 'planned' | 'estimated' }) =>
      base.select(columns, options).eq('org_id', orgId),
    insert: (values: any, options?: { count?: 'exact' | 'planned' | 'estimated' }) =>
      base.insert(withOrgId(values, orgId), options),
    upsert: (values: any, options?: { count?: 'exact' | 'planned' | 'estimated' }) =>
      base.upsert(withOrgId(values, orgId), options),
    update: (values: any, options?: { count?: 'exact' | 'planned' | 'estimated' }) =>
      base.update(values, options).eq('org_id', orgId),
    delete: (options?: { count?: 'exact' | 'planned' | 'estimated' }) =>
      base.delete(options).eq('org_id', orgId),
  };
};

export const supabase = Object.assign(supabaseClient, { fromSecure });
